import React, { useState, useEffect } from 'react'
import {
  SimpleShowLayout,
  TextField,
  EmailField,
  ReferenceField,
  BooleanField,
  useRecordContext,
  useRefresh,
  useNotify,
} from 'react-admin'
import { Chip, Typography, Box, Card, CardContent, Table, TableBody, TableRow, TableCell, useTheme, TableHead, TableContainer, Paper, CircularProgress, Button, Slider, Dialog, DialogTitle, DialogContent, DialogActions, TextField as MuiTextField } from '@mui/material'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'

import Show from '../common/views/Show'
import DateField from '../common/fields/DateField'
import LeadNotes from './LeadNotes'
import { API_URL } from '../../services/settings'
import { leadStatusMap, leadSourceMap, statusColors } from './constants'
import { campaignLeadStatusColors } from '../campaigns/constants'

const LeadTitle = ({ record }) => {
  return <span>{record ? `${record.full_name}` : ''}</span>
}

const StatusField = () => {
  const record = useRecordContext()

  if (!record) return null

  return (
    <Chip
      label={leadStatusMap[record.status] || record.status}
      color={statusColors[record.status] || 'default'}
      size="small"
    />
  )
}

const SourceField = () => {
  const record = useRecordContext()

  if (!record) return null

  return <Typography variant="body2">{leadSourceMap[record.source] || record.source}</Typography>
}

const CampaignHistory = ({ campaigns = [] }) => {
  const record = useRecordContext()

  if (!record || campaigns.length === 0) return null

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString()
  }

  return (
    <Card sx={{ mt: 3, mx: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Campaign History ({campaigns.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Send Method</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Opened</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell sx={{ fontWeight: 500 }}>{campaign.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.lead_status}
                        size="small"
                        color={campaignLeadStatusColors[campaign.lead_status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.send_method}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell variant="caption">{formatDate(campaign.sent_at)}</TableCell>
                    <TableCell variant="caption">{formatDate(campaign.opened_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
      </CardContent>
    </Card>
  )
}

const LeadFormData = () => {
  const record = useRecordContext()
  const theme = useTheme()

  if (!record || !record.form_data) return null

  const formData = record.form_data
  const entries = Object.entries(formData).filter(([key]) => key !== '_history')

  if (entries.length === 0) return null

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }
    return String(value)
  }

  return (
    <Card sx={{ mt: 3, mx: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Form Data
        </Typography>
        <Table size="small">
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow key={key}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '30%',
                    backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : 'action.disabled',
                  }}
                >
                  {key}
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  {renderValue(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

const TemperatureIndicator = ({ lastCampaign = null }) => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const notify = useNotify()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [temperature, setTemperature] = useState(record?.lead_temperature || 50)
  const [expectedRevenue, setExpectedRevenue] = useState(record?.expected_revenue || '')
  const [proposalAmount, setProposalAmount] = useState(record?.proposal_amount || '')

  if (!record) return null

  const getTemperatureColor = (temp) => {
    if (temp >= 70) return '#FF6B35' // Hot - orange/red
    if (temp >= 40) return '#FFA500' // Medium - orange
    return '#1976D2' // Cold - blue
  }

  const getTemperatureLabel = (temp) => {
    if (temp >= 70) return 'Hot'
    if (temp >= 40) return 'Medium'
    return 'Cold'
  }

  const getDaysSinceContact = () => {
    if (!record.last_contacted_at) return null
    const lastContact = new Date(record.last_contacted_at)
    const today = new Date()
    const diffTime = Math.abs(today - lastContact)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token')
      const payload = { lead_temperature: temperature }
      if (expectedRevenue !== '') payload.expected_revenue = parseFloat(expectedRevenue)
      if (proposalAmount !== '') payload.proposal_amount = parseFloat(proposalAmount)

      const response = await fetch(`${API_URL}/leads/${record.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      if (response.ok) {
        notify('Lead updated', { type: 'success' })
        setDialogOpen(false)
        refresh()
      } else {
        notify('Failed to update lead', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    }
  }

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2, backgroundColor: getTemperatureColor(record.lead_temperature), color: 'white' }}>
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Temperature Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LocalFireDepartmentIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Temperature</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {getTemperatureLabel(record.lead_temperature)} ({record.lead_temperature}%)
                </Typography>
              </Box>
            </Box>

            {/* Financial Info - inline */}
            {(record.proposal_amount || record.expected_revenue) && (
              <Box sx={{ display: 'flex', gap: 2, borderLeft: '1px solid rgba(255,255,255,0.3)', pl: 1.5 }}>
                {record.proposal_amount && (
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Proposal</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {record.proposal_amount.toLocaleString()} €
                    </Typography>
                  </Box>
                )}
                {record.expected_revenue && (
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Expected</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {record.expected_revenue.toLocaleString()} €
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Campaign & Dates Info - inline */}
            {(lastCampaign || record.last_contacted_at || record.meeting_scheduled_date) && (
              <Box sx={{ display: 'flex', gap: 2, borderLeft: '1px solid rgba(255,255,255,0.3)', pl: 1.5, flex: 1 }}>
                {lastCampaign && (
                  <Box sx={{ minWidth: 'fit-content' }}>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Campaign</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {lastCampaign.name || 'Unknown'}
                    </Typography>
                    <Chip label={lastCampaign.lead_status} size="small" sx={{ height: 20, backgroundColor: 'rgba(255,255,255,0.3)', color: 'white', fontSize: '0.7rem' }} />
                  </Box>
                )}
                {record.last_contacted_at && (
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Last Contact</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getDaysSinceContact()}d ago
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                      {formatDate(record.last_contacted_at)}
                    </Typography>
                  </Box>
                )}
                {record.meeting_scheduled_date && (
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Meeting</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      📅 {formatDate(record.meeting_scheduled_date)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Edit Button */}
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.3)', color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.5)' }, ml: 'auto' }}
              onClick={() => {
                setTemperature(record.lead_temperature)
                setExpectedRevenue(record.expected_revenue || '')
                setProposalAmount(record.proposal_amount || '')
                setDialogOpen(true)
              }}
            >
              Edit
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Lead Information</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>Temperature: {temperature}%</Typography>
            <Slider
              min={0}
              max={100}
              step={1}
              value={temperature}
              onChange={(e, val) => setTemperature(val)}
              marks={[
                { value: 0, label: 'Cold' },
                { value: 50, label: 'Medium' },
                { value: 100, label: 'Hot' },
              ]}
            />
            <Typography variant="caption" color="textSecondary">
              {temperature >= 70 ? '🔥 Hot - High probability of conversion' : temperature >= 40 ? '⚠️ Medium - Needs nurturing' : '❄️ Cold - Needs more engagement'}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Expected Revenue</Typography>
            <input
              type="number"
              value={expectedRevenue}
              onChange={(e) => setExpectedRevenue(e.target.value)}
              placeholder="Enter expected revenue"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Proposal Amount</Typography>
            <input
              type="number"
              value={proposalAmount}
              onChange={(e) => setProposalAmount(e.target.value)}
              placeholder="Enter proposal amount"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const QuickActions = () => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const notify = useNotify()
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAction, setSelectedAction] = useState(null)
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().slice(0, 16))
  const [proposalAmount, setProposalAmount] = useState('')
  const [contactDate, setContactDate] = useState(new Date().toISOString().slice(0, 16))
  const [contactMethod, setContactMethod] = useState('email')
  const [contactNote, setContactNote] = useState('')

  if (!record) {
    return null
  }

  // Define available actions per status
  // Pipeline: new → contacted → qualified → meeting_scheduled → proposal_sent → converted
  // Terminal states: unqualified, lost
  const statusActions = {
    new: [
      { endpoint: 'mark_contacted', label: 'Mark as Contacted', color: 'primary' },
    ],
    contacted: [
      { endpoint: 'mark_qualified', label: 'Mark as Qualified', color: 'primary' },
      { endpoint: 'mark_unqualified', label: 'Mark as Unqualified', color: 'error' },
      { endpoint: 'mark_nurturing', label: 'Mark as Nurturing', color: 'info' },
      { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
    ],
    qualified: [
      { endpoint: 'mark_meeting_scheduled', label: 'Schedule Meeting', color: 'primary' },
      { endpoint: 'mark_unqualified', label: 'Mark as Unqualified', color: 'error' },
      { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
      { endpoint: 'mark_nurturing', label: 'Back to Nurturing', color: 'info' },
      { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
    ],
    meeting_scheduled: [
      { endpoint: 'mark_proposal_sent', label: 'Send Proposal', color: 'primary' },
      { endpoint: 'mark_unqualified', label: 'Mark as Unqualified', color: 'error' },
      { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
      { endpoint: 'mark_nurturing', label: 'Back to Nurturing', color: 'info' },
      { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
    ],
    proposal_sent: [
      { endpoint: 'mark_converted', label: 'Mark as Converted', color: 'success' },
      { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
      { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
    ],
    nurturing: [
      { endpoint: 'mark_contacted', label: 'Mark as Contacted', color: 'primary' },
      { endpoint: 'mark_qualified', label: 'Mark as Qualified', color: 'primary' },
      { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
      { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
    ],
    on_hold: [
      { endpoint: 'mark_contacted', label: 'Mark as Contacted', color: 'primary' },
      { endpoint: 'mark_qualified', label: 'Mark as Qualified', color: 'primary' },
      { endpoint: 'mark_nurturing', label: 'Back to Nurturing', color: 'info' },
      { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
    ],
  }

  const actions = statusActions[record.status] || []

  if (actions.length === 0) {
    return null
  }

  const handleStatusChange = async (endpoint, extraData = {}) => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/leads/${record.id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: Object.keys(extraData).length > 0 ? JSON.stringify(extraData) : undefined,
      })
      if (response.ok) {
        notify('Lead status updated', { type: 'success' })
        setDialogOpen(false)
        setSelectedAction(null)
        setContactNote('')
        refresh()
      } else {
        notify('Failed to update lead status', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleActionClick = (endpoint) => {
    if (endpoint === 'mark_contacted') {
      setSelectedAction(endpoint)
      setContactDate(new Date().toISOString().slice(0, 16))
      setContactMethod('email')
      setContactNote('')
      setDialogOpen(true)
    } else if (endpoint === 'mark_meeting_scheduled') {
      setSelectedAction(endpoint)
      setMeetingDate(new Date().toISOString().slice(0, 16))
      setDialogOpen(true)
    } else if (endpoint === 'mark_proposal_sent') {
      setSelectedAction(endpoint)
      setProposalAmount(record.proposal_amount || '')
      setDialogOpen(true)
    } else {
      handleStatusChange(endpoint)
    }
  }

  const handleSaveAction = async () => {
    if (selectedAction === 'mark_contacted') {
      try {
        const token = localStorage.getItem('token')
        await fetch(`${API_URL}/leads/${record.id}/mark_contacted`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ contacted_at: contactDate ? new Date(contactDate).toISOString() : null }),
        })
        // Add note about the contact method and date
        if (contactNote || contactMethod) {
          const noteText = `Contacted via ${contactMethod}${contactNote ? ': ' + contactNote : ''}`
          await fetch(`${API_URL}/leads/${record.id}/notes`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ content: noteText }),
          })
        }
        notify('Lead marked as contacted', { type: 'success' })
        setDialogOpen(false)
        setSelectedAction(null)
        refresh()
      } catch (e) {
        notify('Error: ' + e.message, { type: 'error' })
      }
    } else if (selectedAction === 'mark_meeting_scheduled') {
      handleStatusChange(selectedAction, {
        meeting_scheduled_date: meetingDate,
      })
    } else if (selectedAction === 'mark_proposal_sent') {
      handleStatusChange(selectedAction, {
        proposal_amount: parseFloat(proposalAmount) || null,
      })
    }
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {actions.map((action) => (
            <Button
              key={action.endpoint}
              variant="contained"
              color={action.color}
              onClick={() => handleActionClick(action.endpoint)}
              disabled={loading}
              size="small"
            >
              {loading ? 'Updating...' : action.label}
            </Button>
          ))}
        </Box>

        {/* Dialog for Mark as Contacted */}
        <Dialog open={dialogOpen && selectedAction === 'mark_contacted'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Mark as Contacted</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact Date</Typography>
                <input
                  type="datetime-local"
                  value={contactDate}
                  onChange={(e) => setContactDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact Method</Typography>
                <select
                  value={contactMethod}
                  onChange={(e) => setContactMethod(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="email">Email (Manual)</option>
                  <option value="phone">Phone Call</option>
                  <option value="meeting">Meeting</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="kernel">Email (Kernel System)</option>
                  <option value="lemlist">Email (Lemlist)</option>
                  <option value="other">Other</option>
                </select>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Note (Optional)</Typography>
                <textarea
                  value={contactNote}
                  onChange={(e) => setContactNote(e.target.value)}
                  placeholder="Add details about the contact..."
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minHeight: '80px', fontFamily: 'inherit' }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveAction} disabled={loading}>
              {loading ? 'Saving...' : 'Mark as Contacted'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Schedule Meeting */}
        <Dialog open={dialogOpen && selectedAction === 'mark_meeting_scheduled'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Schedule Meeting</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Meeting Date & Time</Typography>
                <input
                  type="datetime-local"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveAction} disabled={loading}>
              {loading ? 'Saving...' : 'Schedule'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Send Proposal */}
        <Dialog open={dialogOpen && selectedAction === 'mark_proposal_sent'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send Proposal</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Proposal Amount</Typography>
                <input
                  type="number"
                  value={proposalAmount}
                  onChange={(e) => setProposalAmount(e.target.value)}
                  placeholder="Enter amount"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveAction} disabled={loading}>
              {loading ? 'Saving...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  )
}

const EnrichButton = () => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const notify = useNotify()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [humanNotes, setHumanNotes] = useState('')

  if (!record || !record.website) return null

  const handleEnrich = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/leads/${record.id}/enrich`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ human_notes: humanNotes || null }),
      })
      const data = await response.json()
      if (response.ok) {
        notify('Lead enriched successfully', { type: 'success' })
        setDialogOpen(false)
        setHumanNotes('')
        refresh()
      } else {
        notify(data.message || 'Enrichment failed', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mx: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={loading}
          size="small"
        >
          Enrich with AI
        </Button>
        {record && record.ai_summary && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AutoFixHighIcon fontSize="small" sx={{ color: 'success.main' }} />
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
              Enriched {record.website_fetched_at && `• ${new Date(record.website_fetched_at).toLocaleDateString('fr-FR')}`}
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>AI Lead Enrichment</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Analyze <strong>{record.website}</strong> and enrich this lead with AI-generated insights (industry, description, potential needs, sales angle).
          </Typography>
          <MuiTextField
            label="Human Notes (optional)"
            placeholder="e.g. Site needs redesign, law firm, saw them at a conference..."
            multiline
            rows={3}
            fullWidth
            value={humanNotes}
            onChange={(e) => setHumanNotes(e.target.value)}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button variant="contained" onClick={handleEnrich} disabled={loading}>
            {loading ? 'Enriching...' : 'Enrich'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const LeadShowContent = () => {
  const record = useRecordContext()
  const [campaigns, setCampaigns] = useState([])

  useEffect(() => {
    if (!record?.id) return
    const fetchCampaigns = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`${API_URL}/leads/${record.id}/campaigns`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (response.ok) {
          const data = await response.json()
          setCampaigns(data.data || [])
        }
      } catch (e) {
        console.error('Error fetching campaigns:', e)
      }
    }
    fetchCampaigns()
  }, [record?.id])

  const lastCampaign = campaigns.length > 0 ? campaigns[0] : null

  return (
    <Box>
      <TemperatureIndicator lastCampaign={lastCampaign} />
      <QuickActions />
      <EnrichButton />
      <SimpleShowLayout>
        {/* TOP: WEBSITE & ID */}
        <TextField source="website" />
        <TextField source="id" />

        {/* CONTACT */}
        <TextField source="first_name" label="First Name" />
        <TextField source="last_name" label="Last Name" />
        <EmailField source="email" />
        <TextField source="phone" />
        <TextField source="mobile" />
        <TextField source="company" />
        <TextField source="job_title" label="Job Title" />
        <TextField source="linkedin_url" label="LinkedIn Profile URL" />

        {/* STATUS & PIPELINE */}
        <StatusField label="Status" />
        <ReferenceField source="customer_id" reference="customers" label="Customer" allowEmpty>
          <TextField source="name" />
        </ReferenceField>
        <SourceField label="Source" />
        <TextField source="campaign" />

        {/* ENRICHMENT */}
        <TextField source="industry" label="Industry" />
        <TextField source="language" label="Language" />
        <TextField source="extracted_tags" label="Tags" />
        <DateField source="website_fetched_at" label="Website Fetched At" />
        <TextField source="ai_summary" label="AI Summary" sx={{ whiteSpace: 'pre-line' }} />

        {/* PROJECT & SALES */}
        <TextField source="project_description" label="Project Description" />
        <TextField source="project_location" label="Project Location" />
        <TextField source="project_timeline" label="Project Timeline" />
        <TextField source="project_budget" label="Project Budget" />
        <TextField source="expected_revenue" label="Expected Revenue" />
        <TextField source="proposal_amount" label="Proposal Amount" />
        <DateField source="meeting_scheduled_date" label="Meeting Scheduled Date" />

        {/* CONTACT HISTORY */}
        <DateField source="first_contacted_at" label="First Contacted At" />
        <DateField source="last_contacted_at" label="Last Contacted At" />
        <DateField source="next_reminder_at" label="Next Reminder" />

        {/* META */}
        <ReferenceField source="created_by_id" reference="users" label="Created By" allowEmpty>
          <TextField source="fullname" />
        </ReferenceField>
        <ReferenceField source="assigned_to_id" reference="users" label="Assigned To" allowEmpty>
          <TextField source="fullname" />
        </ReferenceField>
        <DateField source="created_at" />
        <DateField source="updated_at" />
        <BooleanField source="marketing_consent" label="Marketing Consent" />
      </SimpleShowLayout>
      <Box sx={{ mx: 2 }}>
        <LeadNotes />
      </Box>
      <CampaignHistory campaigns={campaigns} />
      <LeadFormData />
    </Box>
  )
}

const LeadShow = (props) => (
  <Show title={<LeadTitle />} {...props}>
    <LeadShowContent />
  </Show>
)

export default LeadShow
